import React from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useGroq } from "../lib/sanity";
import { imageIdToURL } from "../utils/LinkUtils";
import ArticleBlock from "../components/blog/ArticleBlock";
import { useSeo } from "../utils/useSeo";

const AUTHOR_QUERY = `{
  "author": *[_type == "profile" && _id == $id][0]{
    "first_name": firstName,
    "last_name": lastName,
    avatar
  },
  "articles": *[_type == "article" && authors._ref == $id] | order(publishDate desc){
    title,
    subtitle,
    "publish_date": string::split(publishDate, "T")[0],
    "slug": slug.current,
    sort,
    photo,
    "tags": tags[]->{text, color}
  }
}`;

function AuthorPage() {
  const { id } = useParams();
  const { loading, error, data } = useGroq<any>(AUTHOR_QUERY, { id });

  if (error) {
    toast.error("Failed to load this author's posts. Please try again later.", {
      id: "noserverconnection",
      duration: Infinity,
    });
  }

  const author = data?.author;
  const name = author
    ? [author.first_name, author.last_name].filter(Boolean).join(" ")
    : "Author";
  const articles: any[] = data?.articles || [];
  useSeo({ title: name, description: `Brown Esports articles by ${name}.` });

  return (
    <section className="relative bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 md:pt-40 pb-20">
        <h1 className="text-5xl font-header font-bold text-white mb-2">{name}</h1>
        <p className="text-gray-400 mb-10 border-b border-gray-600 pb-4">
          {loading
            ? "Loading…"
            : `${articles.length} post${articles.length === 1 ? "" : "s"}`}
        </p>

        {!loading && !error && articles.length === 0 ? (
          <p className="text-gray-400">No posts yet.</p>
        ) : null}

        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-6 md:gap-y-8 items-start">
          {!loading && !error
            ? articles.map((item: any) => (
                <ArticleBlock
                  key={"author" + item.slug}
                  title={item.title}
                  subtitle={item.subtitle}
                  link={`/news/${item.publish_date}/${item.slug}`}
                  author_name={name}
                  profile_link={`/author/${id}`}
                  date={new Date(item.publish_date).toLocaleDateString("en-us", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC",
                  })}
                  tags={item.tags}
                  image_link={imageIdToURL(item?.photo)}
                  author_image={author?.avatar}
                />
              ))
            : [...Array(3)].map((_, i) => (
                <div className="flex flex-col h-full" key={"authorskeleton" + i}>
                  <figure className="relative h-0 pb-9/16 bg-gray-600 animate-pulse mb-4"></figure>
                  <figure className="h-6 w-48 bg-gray-600 animate-pulse mb-4"></figure>
                  <figure className="h-4 w-full bg-gray-600 animate-pulse mb-2"></figure>
                </div>
              ))}
        </div>
      </div>
    </section>
  );
}

export default AuthorPage;
